import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Add as AddIcon, 
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  Photo as PhotoIcon,
  PhotoLibrary as PhotoLibraryIcon,
  PhotoLibraryOutlined as PhotoLibraryOutlinedIcon,
  Movie as MovieIcon,
  AttachFile as AttachFileIcon,
  PushPin as PushPinIcon,
  PushPinOutlined as PushPinOutlinedIcon
} from '@mui/icons-material';
import { adminApi } from '../../redux/adminApiSlice';
import { Place, Neighborhood } from '../../types/interfaces';

interface CustomPostCreatorProps {
  onSuccess?: () => void;
}

interface MockPost {
  _id: string;
  text: string;
  locationId: string;
  locationType: 'place' | 'neighborhood' | 'place-type';
  allowMedia?: boolean;
  media?: {
    type: 'photo' | 'video';
    url: string;
    thumbnailUrl?: string;
  };
}

// Interface for place types with the same structure as other locations
interface PlaceType {
  _id: string; // Using the type name as the ID
  name: string;
  type?: string;
}

const CustomPostCreator: React.FC<CustomPostCreatorProps> = ({ onSuccess }) => {
  // Get the API hooks
  const [saveMockPost, { isLoading: isSaving }] = adminApi.useSaveMockPostTextMutation();
  const [deleteMockPost, { isLoading: isDeleting }] = adminApi.useDeleteMockPostTextMutation();
  const [uploadMedia, { isLoading: isUploading }] = adminApi.useUploadMockPostMediaMutation();
  const [deleteMedia, { isLoading: isDeletingMedia }] = adminApi.useDeleteMockPostMediaMutation();
  const [updateAllowMedia] = adminApi.useUpdateMockPostAllowMediaMutation();
  const { data: neighborhoods = [] } = adminApi.useGetMockNeighborhoodsQuery();
  const { data: places = [] } = adminApi.useGetPlacesQuery();
  const { data: placeTypesData = {} } = adminApi.useGetPlaceTypesQuery();
  const { data: savedMockPosts = [], refetch: refetchMockPosts } = adminApi.useGetSavedMockPostsQuery();
  
  // File input reference
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State for UI
  const [locationTab, setLocationTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newPostText, setNewPostText] = useState('');
  const [allowMedia, setAllowMedia] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState<Neighborhood | Place | PlaceType | null>(null);
  const [activePostId, setActivePostId] = useState<string | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [pinnedLocations, setPinnedLocations] = useState<string[]>([]);
  
  // Load pinned locations from localStorage on component mount
  useEffect(() => {
    const savedPins = localStorage.getItem('pinnedLocations');
    if (savedPins) {
      setPinnedLocations(JSON.parse(savedPins));
    }
  }, []);
  
  // Save pinned locations to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('pinnedLocations', JSON.stringify(pinnedLocations));
  }, [pinnedLocations]);
  
  // Bulk import state
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [bulkImportText, setBulkImportText] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  
  // Edit post state
  const [editingPost, setEditingPost] = useState<MockPost | null>(null);
  const [editPostText, setEditPostText] = useState('');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  // Add debounced state for edit text
  const [debouncedEditText, setDebouncedEditText] = useState('');
  
  // Update the debounced value after delay
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEditText(editPostText);
    }, 300);
    
    return () => {
      clearTimeout(handler);
    };
  }, [editPostText]);

  // Memoize the quote detection for performance
  const hasQuotes = useMemo(() => {
    return debouncedEditText.includes('"') || debouncedEditText.includes("'");
  }, [debouncedEditText]);
  
  
  // Filtered mock posts for the selected location
  const mockPostsForLocation = React.useMemo(() => {
    return selectedLocation 
      ? (savedMockPosts as MockPost[]).filter(post => post.locationId === selectedLocation._id)
      : [];
  }, [selectedLocation, savedMockPosts]);

  // Prepare place types for the UI - convert grouped object to array of PlaceType objects
  const formattedPlaceTypes: PlaceType[] = React.useMemo(() => {
    if (!placeTypesData || typeof placeTypesData !== 'object') {
      return [];
    }

    // Use a Set to track unique types and avoid duplicates
    const uniqueTypes = new Set<string>();
    const result: PlaceType[] = [];
    
    Object.entries(placeTypesData).forEach(([category, types]) => {
      if (Array.isArray(types)) {
        types.forEach(type => {
          // Only add if we haven't seen this type before
          if (!uniqueTypes.has(type)) {
            uniqueTypes.add(type);
            result.push({
              _id: type,  // Use the type name as ID
              name: type,
              type: 'place-type'
            });
          }
        });
      }
    });
    
    return result;
  }, [placeTypesData]);

  // Add a new state to track media button state locally for UI optimization
  const [localMediaSettings, setLocalMediaSettings] = useState<Record<string, boolean>>({});

  const handleLocationSelect = (location: Neighborhood | Place | PlaceType) => {
    setSelectedLocation(location);
    setNewPostText(''); // Clear the input when changing locations
  };

  const handleAddPost = async () => {
    if (!selectedLocation) {
      setError('Please select a location first');
      return;
    }

    if (!newPostText.trim()) {
      setError('Please enter some text for the post');
      return;
    }

    try {
      // Determine the location type based on the selected tab and location type
      let locationType: 'place' | 'neighborhood' | 'place-type';
      
      if (locationTab === 0) {
        locationType = 'neighborhood';
      } else if (locationTab === 1) {
        locationType = 'place';
      } else {
        locationType = 'place-type';
      }
      
      await saveMockPost({
        text: newPostText,
        locationId: selectedLocation._id,
        locationType,
        allowMedia
      }).unwrap();
      
      setSuccess('Mock post text saved successfully');
      setNewPostText('');
      refetchMockPosts();
      onSuccess?.();
    } catch (err) {
      console.error('Failed to save mock post text:', err);
      setError('Failed to save mock post text');
    }
  };
  
  const handleEditClick = (post: MockPost) => {
    setEditingPost(post);
    setEditPostText(post.text);
    setAllowMedia(post.allowMedia !== false);
    setIsEditDialogOpen(true);
  };
  
  const handleEditSave = async () => {
    if (!editingPost || !editPostText.trim()) {
      return;
    }
    
    try {
      // Track what changed to minimize API calls
      const textChanged = editingPost.text !== editPostText;
      const allowMediaChanged = (editingPost.allowMedia !== false) !== allowMedia;
      
      if (textChanged) {
        // If text changed, we need to delete and recreate
        await deleteMockPost(editingPost._id).unwrap();
        
        // Create a new post with the updated text
        await saveMockPost({
          text: editPostText,
          locationId: editingPost.locationId,
          locationType: editingPost.locationType,
          allowMedia
        }).unwrap();
      } else if (allowMediaChanged) {
        // If only allowMedia changed, use the dedicated endpoint
        await updateAllowMedia({
          postId: editingPost._id,
          allowMedia
        }).unwrap();
      }
      
      setSuccess('Post updated successfully');
      
      // Close dialog and reset state
      setIsEditDialogOpen(false);
      
      // Delay state reset to avoid UI flickering during dialog close animation
      setTimeout(() => {
        setEditingPost(null);
        setEditPostText('');
      }, 300);
      
      // Only refetch if something actually changed
      if (textChanged || allowMediaChanged) {
        refetchMockPosts();
      }
    } catch (err) {
      console.error('[Error] Failed to update post:', err);
      setError('Failed to update post');
    }
  };

  const handleDeletePost = async (postId: string) => {
    try {
      await deleteMockPost(postId).unwrap();
      setSuccess('Mock post deleted successfully');
      refetchMockPosts();
    } catch (err) {
      console.error('Failed to delete mock post:', err);
      setError('Failed to delete mock post');
    }
  };

  // Parse and save bulk post texts
  const handleBulkImport = async () => {
    if (!selectedLocation) {
      setError('Please select a location first');
      return;
    }

    if (!bulkImportText.trim()) {
      setError('Please enter some text to import');
      return;
    }

    try {
      setIsImporting(true);
      
      // Determine the location type based on the selected tab and location type
      let locationType: 'place' | 'neighborhood' | 'place-type';
      
      if (locationTab === 0) {
        locationType = 'neighborhood';
      } else if (locationTab === 1) {
        locationType = 'place';
      } else {
        locationType = 'place-type';
      }
      
      // Split by lines and filter out empty lines
      const lines = bulkImportText
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      
      setImportProgress({ current: 0, total: lines.length });
      
      for (let i = 0; i < lines.length; i++) {
        // Clean the text by removing quotes and commas at the end
        let text = lines[i];
        
        // Handle quoted strings (remove outer quotes and trailing commas)
        if ((text.startsWith('"') && text.endsWith('"')) || 
            (text.startsWith('"') && text.endsWith('",')) ||
            (text.startsWith("'") && text.endsWith("'")) ||
            (text.startsWith("'") && text.endsWith("',"))) {
          // Remove the enclosing quotes
          text = text.replace(/^["']|["'],?$/g, '');
        }
        
        // Remove any trailing comma that might remain after quote removal
        if (text.endsWith(',')) {
          text = text.slice(0, -1);
        }
        
        // Replace smart quotes with straight quotes before saving
        text = text.replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"');
        
        await saveMockPost({
          text,
          locationId: selectedLocation._id,
          locationType,
          allowMedia
        }).unwrap();
        
        setImportProgress({ current: i + 1, total: lines.length });
      }
      
      setSuccess(`Successfully imported ${lines.length} posts`);
      setBulkImportText('');
      setIsBulkImportOpen(false);
      refetchMockPosts();
    } catch (err) {
      console.error('Failed to import posts:', err);
      setError('Failed to import posts');
    } finally {
      setIsImporting(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, postId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setActivePostId(postId);
    setIsDraggingOver(true);
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };
  
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, postId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    setActivePostId(null);
    
    const files = e.dataTransfer.files;
    if (files.length === 0) return;
    
    const file = files[0];
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      setError('Only image or video files are supported');
      return;
    }
    
    try {
      await uploadMedia({ postId, file }).unwrap();
      setSuccess(`Media added to post successfully`);
      refetchMockPosts();
    } catch (err) {
      console.error('Failed to upload media:', err);
      setError('Failed to upload media');
    }
  };
  
  const handleFileSelect = async (postId: string) => {
    setActivePostId(postId);
    
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !activePostId) {
      setActivePostId(null);
      return;
    }
    
    const file = files[0];
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      setError('Only image or video files are supported');
      setActivePostId(null);
      return;
    }
    
    try {
      await uploadMedia({ postId: activePostId, file }).unwrap();
      setSuccess(`Media added to post successfully`);
      refetchMockPosts();
    } catch (err) {
      console.error('Failed to upload media:', err);
      setError('Failed to upload media');
    } finally {
      setActivePostId(null);
      // Reset file input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleDeleteMedia = async (postId: string) => {
    try {
      await deleteMedia(postId).unwrap();
      setSuccess('Media removed from post');
      refetchMockPosts();
    } catch (err) {
      console.error('Failed to delete media:', err);
      setError('Failed to delete media');
    }
  };

  // Add handler for toggling allowMedia
  const handleToggleAllowMedia = async (post: MockPost) => {
    try {
      // Update local state immediately for a responsive UI
      const newAllowMedia = !(post.allowMedia !== false);
      setLocalMediaSettings({
        ...localMediaSettings,
        [post._id]: newAllowMedia
      });
      
      // Use the new PATCH endpoint to update just the allowMedia property
      setTimeout(async () => {
        try {
          await updateAllowMedia({
            postId: post._id,
            allowMedia: newAllowMedia
          }).unwrap();
          
          // Quietly refetch posts without causing UI flashes
          await refetchMockPosts();
        } catch (err) {
          console.error('[Error] Failed to update media preference:', err);
          // Only show error if this component is still mounted
          setError('Failed to update media preference');
          
          // Revert local state on error
          setLocalMediaSettings(prevSettings => ({
            ...prevSettings,
            [post._id]: post.allowMedia !== false
          }));
        }
      }, 300); // Small delay to prevent accidental double-clicks
    } catch (err) {
      console.error('[Error] Failed to update media preference:', err);
      setError('Failed to update media preference');
      
      // Revert local state on error
      setLocalMediaSettings({
        ...localMediaSettings,
        [post._id]: post.allowMedia !== false
      });
    }
  };

  const handleTogglePin = (location: Neighborhood | Place | PlaceType) => {
    if (pinnedLocations.includes(location._id)) {
      setPinnedLocations(pinnedLocations.filter(id => id !== location._id));
    } else {
      setPinnedLocations([...pinnedLocations, location._id]);
    }
  };
  
  // Helper function to sort locations with pinned items first
  const sortWithPinnedFirst = <T extends { _id: string; name: string }>(items: T[]): T[] => {
    return [...items].sort((a, b) => {
      const isPinnedA = pinnedLocations.includes(a._id);
      const isPinnedB = pinnedLocations.includes(b._id);
      
      if (isPinnedA && !isPinnedB) return -1;
      if (!isPinnedA && isPinnedB) return 1;
      
      // If both are pinned or both are not pinned, sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
  };

  // Memoized post item component to prevent unnecessary rerenders
  const PostItem = React.memo(
    ({ post }: { post: MockPost }) => {
      const isMediaAllowed = localMediaSettings[post._id] !== undefined 
        ? localMediaSettings[post._id] 
        : post.allowMedia !== false;
    
      return (
        <Paper 
          key={post._id} 
          variant="outlined" 
          sx={{ 
            mb: 1, 
            overflow: 'hidden',
            border: activePostId === post._id && isDraggingOver ? '2px dashed #1976d2' : undefined,
            transition: 'all 0.2s'
          }}
          onDragOver={(e) => handleDragOver(e, post._id)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, post._id)}
        >
          <ListItem
            secondaryAction={
              <Box>
                <IconButton 
                  edge="end" 
                  onClick={() => handleEditClick(post)}
                  disabled={isDeleting}
                  sx={{ mr: 1 }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  onClick={() => handleToggleAllowMedia(post)}
                  disabled={isDeleting}
                  color={isMediaAllowed ? 'primary' : 'default'}
                  title={isMediaAllowed ? "Media allowed for this post" : "Media disabled for this post"}
                  sx={{ mr: 1 }}
                >
                  {isMediaAllowed ? <PhotoLibraryIcon /> : <PhotoLibraryOutlinedIcon />}
                </IconButton>
                {post.media ? (
                  <IconButton 
                    edge="end" 
                    onClick={() => handleDeleteMedia(post._id)}
                    disabled={isDeletingMedia}
                    color="error"
                    sx={{ mr: 1 }}
                  >
                    {post.media.type === 'photo' ? <PhotoIcon /> : <MovieIcon />}
                  </IconButton>
                ) : (
                  <IconButton 
                    edge="end" 
                    onClick={() => handleFileSelect(post._id)}
                    disabled={isUploading}
                    sx={{ mr: 1 }}
                  >
                    <AttachFileIcon />
                  </IconButton>
                )}
                <IconButton 
                  edge="end" 
                  onClick={() => handleDeletePost(post._id)}
                  disabled={isDeleting}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              {post.media && (
                <Box sx={{ mr: 2, width: 80, height: 80, flexShrink: 0 }}>
                  {post.media.type === 'photo' ? (
                    <img 
                      src={post.media.url} 
                      alt="Post media" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }}
                    />
                  ) : (
                    <Box sx={{ 
                      width: '100%', 
                      height: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: 'black',
                      borderRadius: 1
                    }}>
                      <MovieIcon sx={{ color: 'white', fontSize: 40 }} />
                    </Box>
                  )}
                </Box>
              )}
              <ListItemText
                primary={post.text}
              />
            </Box>
          </ListItem>
        </Paper>
      );
    },
    // Add a custom equality function that only causes rerenders when we need them
    (prevProps, nextProps) => {
      // If the post IDs are different, definitely rerender
      if (prevProps.post._id !== nextProps.post._id) {
        return false;
      }

      // Check if media changed
      const prevHasMedia = !!prevProps.post.media;
      const nextHasMedia = !!nextProps.post.media;
      if (prevHasMedia !== nextHasMedia) {
        return false;
      }

      // If both have media, check if they're different media items
      if (prevHasMedia && nextHasMedia) {
        if (prevProps.post.media?.url !== nextProps.post.media?.url ||
            prevProps.post.media?.type !== nextProps.post.media?.type) {
          return false;
        }
      }

      // Check if the post text changed
      if (prevProps.post.text !== nextProps.post.text) {
        return false;
      }

      // Don't rerender for allowMedia changes (handled by localMediaSettings)
      
      // Return true to prevent rerender
      return true;
    }
  );

  // Render each location/place/placeType list
  const renderLocationsList = () => {
    if (locationTab === 0) {
      const sortedNeighborhoods = sortWithPinnedFirst(neighborhoods);
      return sortedNeighborhoods.map((neighborhood) => (
        <ListItem 
          key={neighborhood._id} 
          disablePadding
          sx={{ 
            bgcolor: selectedLocation?._id === neighborhood._id ? 'action.selected' : undefined 
          }}
          onClick={() => handleLocationSelect(neighborhood)}
        >
          <Box
            sx={{ 
              display: 'flex',
              width: '100%',
              pl: 2, 
              py: 1,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1">
                  {neighborhood.name}
                  {savedMockPosts.some(post => post.locationId === neighborhood._id) && (
                    <span style={{ fontWeight: 'normal', color: 'primary.main' }}>
                      {' '}({savedMockPosts.filter(post => post.locationId === neighborhood._id).length})
                    </span>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {neighborhood.cityName || 'Neighborhood'}
                </Typography>
              </Box>
              <IconButton 
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTogglePin(neighborhood);
                }}
                color={pinnedLocations.includes(neighborhood._id) ? 'primary' : 'default'}
                sx={{ ml: 1 }}
              >
                {pinnedLocations.includes(neighborhood._id) ? <PushPinIcon /> : <PushPinOutlinedIcon />}
              </IconButton>
            </Box>
          </Box>
        </ListItem>
      ));
    } else if (locationTab === 1) {
      const sortedPlaces = sortWithPinnedFirst(places);
      return sortedPlaces.map((place) => (
        <ListItem 
          key={place._id} 
          disablePadding
          sx={{ 
            bgcolor: selectedLocation?._id === place._id ? 'action.selected' : undefined 
          }}
          onClick={() => handleLocationSelect(place)}
        >
          <Box
            sx={{ 
              display: 'flex',
              width: '100%',
              pl: 2, 
              py: 1,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1">
                  {place.name}
                  {savedMockPosts.some(post => post.locationId === place._id) && (
                    <span style={{ fontWeight: 'normal', color: 'primary.main' }}>
                      {' '}({savedMockPosts.filter(post => post.locationId === place._id).length})
                    </span>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {place.type || 'Place'}
                </Typography>
              </Box>
              <IconButton 
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTogglePin(place);
                }}
                color={pinnedLocations.includes(place._id) ? 'primary' : 'default'}
                sx={{ ml: 1 }}
              >
                {pinnedLocations.includes(place._id) ? <PushPinIcon /> : <PushPinOutlinedIcon />}
              </IconButton>
            </Box>
          </Box>
        </ListItem>
      ));
    } else {
      const sortedPlaceTypes = sortWithPinnedFirst(formattedPlaceTypes);
      return sortedPlaceTypes.map((placeType) => (
        <ListItem 
          key={placeType._id} 
          disablePadding
          sx={{ 
            bgcolor: selectedLocation?._id === placeType._id ? 'action.selected' : undefined 
          }}
          onClick={() => handleLocationSelect(placeType)}
        >
          <Box
            sx={{ 
              display: 'flex',
              width: '100%',
              pl: 2, 
              py: 1,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body1">
                  {placeType.name}
                  {savedMockPosts.some(post => post.locationId === placeType._id) && (
                    <span style={{ fontWeight: 'normal', color: 'primary.main' }}>
                      {' '}({savedMockPosts.filter(post => post.locationId === placeType._id).length})
                    </span>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Place Type
                </Typography>
              </Box>
              <IconButton 
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTogglePin(placeType);
                }}
                color={pinnedLocations.includes(placeType._id) ? 'primary' : 'default'}
                sx={{ ml: 1 }}
              >
                {pinnedLocations.includes(placeType._id) ? <PushPinIcon /> : <PushPinOutlinedIcon />}
              </IconButton>
            </Box>
          </Box>
        </ListItem>
      ));
    }
  };
  
  // Edit dialog optimized for better performance
  const renderEditDialog = () => (
    <Dialog 
      open={isEditDialogOpen} 
      onClose={() => setIsEditDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit Post</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          multiline
          rows={4}
          fullWidth
          variant="outlined"
          defaultValue={editPostText}
          onBlur={(e) => setEditPostText(e.target.value)}
          onChange={(e) => {
            e.currentTarget.setAttribute('data-value', e.target.value);
          }}
          onKeyDown={(e) => {
            // Handle Ctrl+Enter or Cmd+Enter to save
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
              setEditPostText((e.target as HTMLInputElement).value);
              handleEditSave();
            }
          }}
          sx={{ mt: 2 }}
          InputProps={{
            sx: {
              '& .MuiInputBase-input': {
                overflow: 'auto',
                lineHeight: 1.5,
              }
            }
          }}
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <IconButton
            onClick={() => setAllowMedia(!allowMedia)}
            color={allowMedia ? 'primary' : 'default'}
            sx={{ mr: 1 }}
          >
            {allowMedia ? <PhotoLibraryIcon /> : <PhotoLibraryOutlinedIcon />}
          </IconButton>
          <Typography variant="body2">
            {allowMedia ? "Media enabled for this post" : "Media disabled for this post"}
          </Typography>
        </Box>
        
        {/* Use debounced value for expensive validation */}
        {hasQuotes && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> Your text contains quotation marks. These will be included in the actual post. 
              Remove them if they're not intended to be part of the post content.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsEditDialogOpen(false)}>
          Cancel
        </Button>
        <Button 
          onClick={handleEditSave} 
          color="primary"
          disabled={!editPostText.trim() || isSaving}
          startIcon={isSaving && <CircularProgress size={16} />}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ 
      width: '100%',
      height: '100%',
      display: 'flex'
    }}>
      {/* Left Pane - Locations */}
      <Box sx={{ 
        width: 320, 
        height: '100%',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '100%',
        overflow: 'hidden'
      }}>
        <Tabs
          value={locationTab}
          onChange={(_, newValue) => {
            setLocationTab(newValue);
            setSelectedLocation(null); // Clear selection when changing tabs
          }}
          sx={{ 
            borderBottom: '1px solid', 
            borderColor: 'divider',
            minHeight: '48px'
          }}
        >
          <Tab label="Neighborhoods" />
          <Tab label="Places" />
          <Tab label="Place Types" />
        </Tabs>
        
        {/* Explicitly limit this container's height and make it scrollable */}
        <Box sx={{ 
          height: 'calc(100% - 48px)',
          maxHeight: 'calc(100% - 48px)',
          overflow: 'auto',
          bgcolor: 'background.paper'
        }}>
          <List>
            {renderLocationsList()}
          </List>
        </Box>
      </Box>

      {/* Right Pane - Post Management */}
      <Box sx={{ 
        flex: 1, 
        height: '100%', 
        maxHeight: '100%',
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 2,
          maxHeight: 'calc(100% - 120px)' // Subtract form area height
        }}>
          {!selectedLocation ? (
            <Typography 
              sx={{ textAlign: 'center', color: 'text.secondary', mt: 4 }}
            >
              Select a location from the left panel to manage mock posts
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Mock Posts for {selectedLocation.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {mockPostsForLocation.some(post => post.text.includes('"') || post.text.includes("'")) && (
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() => setError('Found posts with quotes. Would you like to remove them?')}
                      startIcon={<EditIcon />}
                    >
                      Fix Quotes
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setIsBulkImportOpen(true)}
                    disabled={!selectedLocation}
                  >
                    Bulk Import
                  </Button>
                </Box>
              </Box>
              
              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}
              
              {/* Hidden file input for media uploads */}
              <input
                type="file"
                accept="image/*,video/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileInputChange}
              />
              
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Drag and drop images or videos onto a post to attach them. Media attached to posts will be preserved and used when generating mock posts.
                </Typography>
              </Alert>

              {mockPostsForLocation.length > 0 ? (
                <List>
                  {mockPostsForLocation.map((post: MockPost) => <PostItem key={post._id} post={post} />)}
                </List>
              ) : (
                <Typography color="text.secondary" sx={{ my: 2 }}>
                  No mock posts saved for this location yet. Add one below or use the Bulk Import.
                </Typography>
              )}
            </>
          )}
        </Box>

        {selectedLocation && (
          <Box sx={{ 
            p: 2, 
            borderTop: 1, 
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}>
            <Typography variant="subtitle2" gutterBottom>
              Add New Mock Post
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                placeholder="Enter mock post text..."
                value={newPostText}
                onChange={(e) => setNewPostText(e.target.value)}
                disabled={!selectedLocation || isSaving}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mr: 1 }}>
                <IconButton
                  onClick={() => setAllowMedia(!allowMedia)}
                  color={allowMedia ? 'primary' : 'default'}
                  title={allowMedia ? "Media enabled" : "Media disabled"}
                >
                  {allowMedia ? <PhotoLibraryIcon /> : <PhotoLibraryOutlinedIcon />}
                </IconButton>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddPost}
                disabled={!selectedLocation || !newPostText.trim() || isSaving}
                startIcon={isSaving ? <CircularProgress size={16} /> : <AddIcon />}
                sx={{ minWidth: '100px' }}
              >
                {isSaving ? 'Saving...' : 'Add'}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      
      {/* Bulk Import Dialog */}
      <Dialog 
        open={isBulkImportOpen} 
        onClose={() => !isImporting && setIsBulkImportOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk Import Posts for {selectedLocation?.name}</DialogTitle>
        <DialogContent>
          {isImporting && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress variant="determinate" value={(importProgress.current / importProgress.total) * 100} />
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                Importing {importProgress.current} of {importProgress.total} posts...
              </Typography>
            </Box>
          )}
          
          <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
            Paste multiple posts below, one per line. Quotes and commas will be automatically removed.
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={10}
            fullWidth
            variant="outlined"
            placeholder={`"Post text 1",\n"Post text 2",\n"Post text 3",`}
            value={bulkImportText}
            onChange={(e) => setBulkImportText(e.target.value)}
            disabled={isImporting}
          />
          
          {/* Add explanation about quotes */}
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> If you're pasting text from ChatGPT or other sources that include quotation marks, 
              they will be automatically removed during import. The actual posts created from these templates 
              will not contain any quotes.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBulkImportOpen(false)} disabled={isImporting}>
            Cancel
          </Button>
          <Button 
            onClick={handleBulkImport} 
            color="primary"
            disabled={!bulkImportText.trim() || isImporting}
            startIcon={isImporting && <CircularProgress size={16} />}
          >
            {isImporting ? 'Importing...' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Use the renderEditDialog function instead of inline JSX */}
      {renderEditDialog()}
      
      {/* Add a Fix Quotes button if any posts have quotes */}
      {mockPostsForLocation.some(post => post.text.includes('"') || post.text.includes("'")) && (
        <Dialog
          open={!!error && error.includes('quotes')}
          onClose={() => setError(null)}
        >
          <DialogTitle>Posts With Quotation Marks</DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              Some of your saved posts contain quotation marks that were likely not intended to be part of the post content.
              Would you like to automatically remove these quotes from all affected posts?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setError(null)} color="secondary">
              Cancel
            </Button>
            <Button 
              onClick={async () => {
                setError(null);
                
                // Get all posts with quotes
                const postsWithQuotes = mockPostsForLocation.filter(
                  post => post.text.includes('"') || post.text.includes("'")
                );
                
                let fixedCount = 0;
                
                // Fix each post
                for (const post of postsWithQuotes) {
                  try {
                    // Remove the old post
                    await deleteMockPost(post._id).unwrap();
                    
                    // Create fixed version
                    let fixedText = post.text;
                    
                    // Handle common quote patterns
                    if ((fixedText.startsWith('"') && fixedText.endsWith('"')) || 
                        (fixedText.startsWith('"') && fixedText.endsWith('",')) ||
                        (fixedText.startsWith("'") && fixedText.endsWith("'")) ||
                        (fixedText.startsWith("'") && fixedText.endsWith("',"))) {
                      // Remove the enclosing quotes
                      fixedText = fixedText.replace(/^["']|["'],?$/g, '');
                    }
                    
                    // Remove any trailing comma that might remain after quote removal
                    if (fixedText.endsWith(',')) {
                      fixedText = fixedText.slice(0, -1);
                    }
                    
                    console.log(`Fixing post: "${post.text}" -> "${fixedText}"`);
                    
                    await saveMockPost({
                      text: fixedText,
                      locationId: post.locationId,
                      locationType: post.locationType,
                      allowMedia: post.allowMedia !== false
                    }).unwrap();
                    
                    fixedCount++;
                  } catch (err) {
                    console.error('Failed to fix post with quotes:', err);
                  }
                }
                
                setSuccess(`Fixed quotes in ${fixedCount} posts`);
                refetchMockPosts();
              }}
              color="primary"
            >
              Fix All Posts
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default CustomPostCreator; 